import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Label,
} from "@fluentui/react-components";
import { Mail20Filled } from "@fluentui/react-icons";
import { useState, useEffect } from "react";
import * as Yup from "yup";
import apiServices from "../../../service";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import { createUser } from "../../../redux/clientPortal/clientPortalslice";
import {
  setPartnerActiveStatus,
  setClientActiveStatus,
  fetchPartnerData,
  updateClientEmail,
} from "../../../redux/clientPortal/clientPartnerSlice";
import "./style.css";

type CreateAccountPopupProps = {
  open: boolean;
  onClose: () => void;
  portalTarget: {
    id: string;
    emailid: string | null;
    type: "client" | "partner";
  } | null;
  notify: any;
  customerId: any;
};

const emailSchema = Yup.string()
  .email("Invalid email format.")
  .required("Email is required.");

function CreateAccountPopup({
  open,
  onClose,
  notify,
  portalTarget,
  customerId,
}: CreateAccountPopupProps) {
  const dispatch: AppDispatch = useDispatch();
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [actionProcessing, setActionProcessing] = useState(false);

  const selectedEmail = portalTarget?.emailid;
  const idToUse = portalTarget?.id;
  const targetType = portalTarget?.type;

  useEffect(() => {
    if (open) {
      if (selectedEmail) {
        setShowConfirmDialog(true);
        setShowEmailDialog(false);
      } else {
        setShowEmailDialog(true);
        setShowConfirmDialog(false);
      }
    } else {
      setShowEmailDialog(false);
      setShowConfirmDialog(false);
      setEmail("");
      setEmailError(null);
    }
  }, [open, selectedEmail]);

  const validateEmail = async (value: string) => {
    try {
      await emailSchema.validate(value);
      setEmailError(null);
    } catch (err: any) {
      setEmailError(err.message);
    }
  };


  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    validateEmail(value);
  };


  const handleCreatePortal = async () => {
    setActionProcessing(true);
    if (!idToUse) {
      notify("Invalid target ID", "", "error");
      return;
    }

    try {
      const resultAction = await dispatch(createUser({ customerId: idToUse }));

      if (
        createUser.fulfilled.match(resultAction) &&
        !resultAction.payload?.isError
      ) {
        notify(resultAction.payload?.message, "", "success");

        if (targetType === "client") {
          dispatch(setClientActiveStatus(true));
        } else if (targetType === "partner") {
          dispatch(setPartnerActiveStatus(true));
          dispatch(fetchPartnerData(idToUse));
        }

        setShowConfirmDialog(false);
        setActionProcessing(false);

        setTimeout(() => {
          onClose();
        }, 5000);
      } else {
        notify(
          resultAction.payload?.message || "Failed to create portal user",
          "",
          "error"
        );
        setActionProcessing(false);
        setShowConfirmDialog(false);
      }
    } catch (err) {
      console.error("Unexpected error creating portal user", err);
      notify("An unexpected error occurred", "", "error");
      setActionProcessing(false);
    }
  };

  const handleUpdateEmail = async () => {
    setActionProcessing(true);

    if (!idToUse) {
      notify("Invalid target ID", "", "error");
      return;
    }

    try {
      const contactUpdate = { email };

      const response: any = await apiServices.client.updateClient(idToUse, {
        contact: contactUpdate,
      });
      if (response.data && !response.data.isError) {
        notify("Email updated successfully", "", "success");
        dispatch(updateClientEmail(email));
        setActionProcessing(false);

        setShowEmailDialog(false);
        setShowConfirmDialog(true);
      } else {
        notify("Failed to update email", "", "error");
        setActionProcessing(false);
      }
    } catch (err) {
      console.error("Error updating email", err);
      setActionProcessing(false);
      notify("An error occurred while updating email", "", "error");
    }
  };

  return (
    <>
      {showEmailDialog && (
        <Dialog
          open={showEmailDialog}
          onOpenChange={(e, data) => !data.open && onClose()}
          modalType="non-modal"
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                Enter {targetType === "client" ? "Client" : "Partner"} Email
              </DialogTitle>
              <DialogContent>
                Please provide the email for creating the account.
                <div className="display_grid">
                  <Label>Email</Label>
                  <div className="cp_input_wrapper">
                    <Input
                      value={email}
                      contentBefore={<Mail20Filled />}
                      placeholder={`Enter ${
                        targetType === "client" ? "Client" : "Partner"
                      } Email`}
                      onChange={handleEmailChange}
                      style={{ marginTop: "5px", width: "100%" }}
                    />
                    {emailError && (
                      <p style={{ color: "red", fontSize: "12px" }}>
                        {emailError}
                      </p>
                    )}
                  </div>
                </div>
                <div className="cp_group_buttons" style={{ margin: "10px 0" }}>
                  <Button
                    appearance="primary"
                    className="asc-button-primary"
                    onClick={handleUpdateEmail}
                    disabled={actionProcessing || !email || !!emailError}
                  >
                    {actionProcessing ? "Saving..." : "Save"}
                  </Button>
                  <Button appearance="outline" onClick={onClose}>
                    Cancel
                  </Button>
                </div>
              </DialogContent>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}

      {showConfirmDialog && (
        <Dialog
          open={showConfirmDialog}
          onOpenChange={(e, data) => !data.open && onClose()}
          modalType="non-modal"
        >
          <DialogSurface>
            <DialogBody>
              <DialogTitle>
                Confirm {targetType === "client" ? "Client" : "Partner"} Portal
                Creation
              </DialogTitle>
              <DialogContent>
                Do you want to create a portal account for{" "}
                <b>{selectedEmail || email}</b>?
                <div className="cp_group_buttons" style={{ margin: "10px 0" }}>
                  <Button
                    appearance="primary"
                    className="asc-button-primary"
                    onClick={handleCreatePortal}
                    disabled={actionProcessing}
                  >
                    {actionProcessing
                      ? "Creating Account..."
                      : "Create Account"}
                  </Button>
                  <Button appearance="outline" onClick={onClose}>
                    Cancel
                  </Button>
                </div>
              </DialogContent>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </>
  );
}

export default CreateAccountPopup;
